<template>
  <div class="flex gap-1">
    <div class="w-96 border relative">
      <div class="flex items-center gap-2 absolute top-3 right-2 z-50">
        <a class="el-icon-circle-plus text-base" @click="add" />
        <a class="el-icon-s-data text-base" @click="summary" />
      </div>
      <el-tabs v-model="activePlan" class="dense">
        <el-tab-pane
          v-for="tab in planTabs"
          :key="tab.name"
          :label="tab.name.capitalize()"
          :name="tab.name"
        >
          <ul class="trigger-marker grow overflow-auto h-[calc(100vh_-_160px)]">
            <li
              v-for="item in tab.tenants"
              :key="item.id"
              class="relative"
              :class="{
                inactive: !item.active,
                active: tenant.id == item.id,
                bold: item.track_version.count > 0,
              }"
            >
              <div class="flex items-center justify-between">
                <div class="grow truncate" @click.stop="load(item)">
                  {{ item.name }}
                </div>
                <div class="flex items-center gap-2">
                  <div class="green" v-if="item.broker">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 1.44 1.44"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="m0.134 0.385 0.533 -0.113a0.06 0.06 0 0 1 0.045 0.008l0.243 0.158a0.06 0.06 0 0 0 0.045 0.008l0.122 -0.026a0.06 0.06 0 0 1 0.071 0.046l0.019 0.088a0.06 0.06 0 0 1 -0.046 0.071l-0.21 0.045a0.06 0.06 0 0 1 -0.045 -0.008l-0.243 -0.158a0.06 0.06 0 0 0 -0.045 -0.008l-0.093 0.02"
                        stroke="currentColor"
                        stroke-width="0.12"
                        stroke-linecap="round"
                      />
                      <path
                        d="m1.306 0.995 -0.533 0.113a0.06 0.06 0 0 1 -0.045 -0.008l-0.243 -0.158a0.06 0.06 0 0 0 -0.045 -0.008l-0.122 0.026a0.06 0.06 0 0 1 -0.071 -0.046l-0.019 -0.088a0.06 0.06 0 0 1 0.046 -0.071l0.21 -0.045a0.06 0.06 0 0 1 0.045 0.008l0.243 0.158a0.06 0.06 0 0 0 0.045 0.008l0.093 -0.02"
                        stroke="currentColor"
                        stroke-width="0.12"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>
                  <div class="blue" v-if="item.directory">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 1.08 1.08"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        class="clr-i-outline--badged clr-i-outline-path-1--badged"
                        d="M.9.405V.87H.18V.39h.219a.06.06 0 0 0 .06-.06H.18V.21h.195l.078.108A.03.03 0 0 0 .477.33h.255A.2.2 0 0 1 .693.27H.492L.423.175A.06.06 0 0 0 .375.15H.18a.06.06 0 0 0-.06.06v.66a.06.06 0 0 0 .06.06H.9A.06.06 0 0 0 .96.87V.397A.2.2 0 0 1 .9.405"
                        fill="currentColor"
                      />
                      <path
                        class="clr-i-outline--badged clr-i-outline-path-2--badged clr-i-badge"
                        d="M1.05.18A.15.15 0 0 1 .9.33.15.15 0 0 1 .75.18a.15.15 0 0 1 .3 0"
                        fill="currentColor"
                      />
                      <path d="M0 0h1.08v1.08H0z" fill="none" />
                    </svg>
                  </div>

                  <el-popover placement="right" width="400" trigger="click">
                    <el-form size="mini" class="e-form" label-position="top">
                      <div
                        class="flex items-center gap-4 bg-slate-100 -mx-1 border-b"
                      >
                        <a @click="openSubdomain(item)" class="no-underline">
                          <i class="el-icon-link" />Website
                        </a>
                        <el-checkbox
                          v-model="item.active"
                          @change="update(item)"
                        >
                          Active
                        </el-checkbox>
                        <el-checkbox
                          v-model="item.directory"
                          @change="update(item)"
                        >
                          Directory
                        </el-checkbox>
                      </div>
                      <el-form-item label="Name">
                        <el-input v-model="item.name" @change="update(item)" />
                      </el-form-item>
                      <el-form-item label="USDOT No.">
                        <el-input
                          v-model="item.us_dot_number"
                          @change="update(item)"
                        />
                      </el-form-item>
                      <el-form-item label="Legacy USDOT No.">
                        <el-input
                          v-model="item.legacy_us_dot_numbers"
                          @change="update(item)"
                        />
                      </el-form-item>

                      <el-form-item label="Tags">
                        <el-select
                          v-model="item.tags"
                          multiple
                          filterable
                          allow-create
                          default-first-option
                          placeholder="Create Tags"
                          class="w-full"
                          @change="update(item)"
                        >
                          <el-option
                            v-for="item in options.tags"
                            :key="item"
                            :label="item"
                            :value="item"
                          >
                          </el-option>
                        </el-select>
                      </el-form-item>
                      <el-form-item label="Stats">
                        <table class="border tbl">
                          <tr>
                            <th width="120">Registered</th>
                            <td>{{
                              item.registered | usFormat({ year: "yy" })
                            }}</td>
                          </tr>
                          <tr>
                            <th>Track Versions</th>
                            <td>
                              <div class="flex items-center justify-between">
                                <span>{{ item.track_version.count }}</span>
                                <i class="text-xs">1 week</i>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <th>Last Track Version</th>
                            <td>{{ item.track_version.time }}</td>
                          </tr>
                          <tr v-if="item.hubs.length > 0">
                            <th width="120">Hubs</th>
                            <td>{{ item.hubs.join(", ") }}</td>
                          </tr>
                        </table>
                      </el-form-item>
                      <el-form-item>
                        <el-popconfirm
                          title="Are you sure to delete this tenant?"
                          confirmButtonText="Confirm"
                          cancelButtonText="Cancel"
                          @confirm="destroy(item)"
                        >
                          <el-button
                            :disabled="item.active"
                            type="danger"
                            slot="reference"
                            class="w-full"
                            v-on:click.stop
                            >Destroy Tenant</el-button
                          >
                        </el-popconfirm>
                      </el-form-item>
                    </el-form>
                    <i slot="reference" class="el-icon-warning-outline" />
                  </el-popover>
                </div>
              </div>
              <div class="absolute bottom-0 left-0 w-full">
                <el-progress
                  :percentage="trackVersionProgress(item)"
                  :show-text="false"
                  :stroke-width="1"
                />
              </div>
            </li>
          </ul>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div class="grow tenant" :key="tenant.id">
      <slot v-if="tenant.id == 0">
        <el-card class="dense w400">
          <div slot="header" class="clearfix">New Tenant</div>
          <div class="e-form p-2">
            <errors v-if="tenant.errors" :errors="tenant.errors"></errors>
            <el-form size="mini" label-position="left" label-width="100px">
              <el-form-item label="Name">
                <el-tooltip
                  class="item"
                  effect="dark"
                  content="Trucking Compamy Name"
                  placement="right"
                  :open-delay="1000"
                >
                  <el-input v-model="tenant.name" />
                </el-tooltip>
              </el-form-item>
              <el-form-item label="USDOT">
                <el-input v-model="tenant.us_dot_number" />
              </el-form-item>
              <el-form-item label="Sub-Domain">
                <el-tooltip
                  class="item"
                  effect="dark"
                  content="Use SCAC (e.g. subdomain: abc, then system access url is https://abc.truckerzoom.com)"
                  placement="right"
                  :open-delay="1000"
                >
                  <el-input v-model="tenant.subdomain" />
                </el-tooltip>
              </el-form-item>
              <el-form-item label="Admin Name">
                <el-input v-model="tenant.admin_name" />
              </el-form-item>
              <el-form-item label="Admin Email">
                <el-input v-model="tenant.admin_email" />
              </el-form-item>
              <el-form-item label="Time Zone">
                <el-select
                  v-model="tenant.time_zone"
                  filterable
                  style="width: 100%"
                >
                  <el-option
                    v-for="item in options.timezones"
                    :key="item"
                    :label="item"
                    :value="item"
                  >
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="">
                <el-checkbox v-model="tenant.carrier_partner">
                  Carrier Partner
                </el-checkbox>
              </el-form-item>
              <div class="bg-white p-2 my-2 border rounded">
                <el-form-item label="Phone">
                  <el-input v-model="tenant.phone" class="us-phone" clearable />
                </el-form-item>
                <el-form-item label="Street">
                  <el-input v-model="tenant.address" />
                </el-form-item>
                <el-form-item label="City">
                  <el-input v-model="tenant.city" />
                </el-form-item>
                <el-form-item label="State">
                  <el-select
                    v-model="tenant.state"
                    filterable
                    style="width: 100%"
                  >
                    <el-option
                      v-for="state in states"
                      :key="state[0]"
                      :label="state[0]"
                      :value="state[0]"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item label="Zip Code">
                  <el-input v-model="tenant.zip" />
                </el-form-item>
              </div>
              <el-form-item>
                <el-button type="primary" @click="create">
                  Create Tenant
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </slot>
      <slot v-if="tenant.id > 0">
        <div class="tenant-name">
          {{ tenant.name }}
        </div>
        <el-tabs
          v-if="tenant.active"
          v-model="tenant.activeTab"
          type="border-card"
        >
          <el-tab-pane label="Settings" name="settings">
            <el-row :gutter="20">
              <el-col :xs="24" :sm="20" :md="18" :lg="14" :xl="10">
                <el-form label-position="top" label-width="100px" size="mini">
                  <label class="title">
                    <div class="text-base">Features</div>
                  </label>
                  <el-form-item>
                    <fieldset>
                      <legend>Enabled</legend>
                      <el-checkbox v-model="envs.broker" label="Broker" />
                      <el-checkbox
                        v-model="envs.carrier_partner"
                        label="Carrier Partner"
                      />
                      <el-checkbox v-model="envs.im" label="IM (app)" />
                      <el-checkbox v-model="envs.sms" label="SMS (twilio)" />
                      <el-checkbox
                        v-model="envs.general_ledger"
                        label="General Ledger"
                      />
                      <el-tooltip
                        content="Enable task status auto transition"
                        placement="right"
                        :open-delay="1000"
                      >
                        <el-checkbox
                          v-model="envs.auto_tasks"
                          label="Auto Tasks"
                        />
                      </el-tooltip>
                      <el-tooltip
                        content="Enable Trip Plans"
                        placement="right"
                        :open-delay="1000"
                      >
                        <el-checkbox
                          v-model="envs.trip_plans"
                          label="Trip Plans"
                        />
                      </el-tooltip>
                      <el-tooltip
                        content="Enable Smart Route"
                        placement="right"
                        :open-delay="1000"
                      >
                        <el-checkbox
                          v-model="envs.smart_routes"
                          label="Smart Routes"
                        />
                      </el-tooltip>
                      <el-checkbox
                        v-model="envs.cloudwatch_logs"
                        label="Cloudwatch Logs"
                      />
                      <el-checkbox
                        v-model="envs.user_interactor"
                        label="User Interactor"
                      />
                      <el-checkbox
                        v-model="envs.loyalty_rewards"
                        label="Loyalty Rewards"
                      />
                      <el-checkbox
                        v-model="envs.marketplace"
                        label="Marketplace"
                      />
                      <el-checkbox
                        v-model="envs.street_turn"
                        label="Street Turn"
                      />
                      <el-checkbox v-model="envs.xmap" label="XMap" />
                      <el-checkbox
                        v-model="envs.calendar_legacy"
                        label="Calendar Legacy"
                      />
                      <el-checkbox
                        v-model="envs.limit_single_editor"
                        label="Limit Single Editor"
                      />
                      <el-checkbox
                        v-model="envs.quote_engine_mileage_complex"
                        label="QE Mileage Complex"
                      />
                      <el-checkbox v-model="envs.tickets" label="Tickets" />
                    </fieldset>
                    <fieldset>
                      <legend>Disabled</legend>
                      <el-tooltip
                        content="Disallow user to add/edit order"
                        placement="right"
                        :open-delay="1000"
                      >
                        <el-checkbox
                          v-model="envs.order_disabled"
                          label="Order Disabled"
                        />
                      </el-tooltip>
                      <el-tooltip
                        content="Disallow user to accounting"
                        placement="right"
                        :open-delay="1000"
                      >
                        <el-checkbox
                          v-model="envs.accounting_disabled"
                          label="Accounting Disabled"
                        />
                      </el-tooltip>
                      <el-tooltip
                        content="Disallow user to upload files"
                        placement="right"
                        :open-delay="1000"
                      >
                        <el-checkbox
                          v-model="envs.uploader_disabled"
                          label="Uploader Disabled"
                        />
                      </el-tooltip>
                    </fieldset>
                    <fieldset>
                      <legend>Navigation</legend>
                      <div class="my-1">
                        <el-select v-model="envs.navigation" size="mini">
                          <el-option label="External" value="external" />
                          <el-option label="HereMap" value="heremap" />
                        </el-select>
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>Document AI Service</legend>
                      <div class="my-1">
                        <el-checkbox-group
                          v-model="envs.document_ai_options"
                          size="mini"
                        >
                          <div class="flex items-center gap-2">
                            <el-checkbox label="open_ai">
                              OpenAI with OCR
                            </el-checkbox>
                            <el-checkbox label="open_ai_vision">
                              OpenAI with Vision
                            </el-checkbox>
                            <el-checkbox label="anthropic">
                              Anthropic
                            </el-checkbox>
                          </div>
                        </el-checkbox-group>
                      </div>
                    </fieldset>
                  </el-form-item>
                  <slot v-if="envs.sms">
                    <el-divider />
                    <label class="title">
                      <div class="text-base">Twilio Settings</div>
                      <el-popover placement="right" width="300" trigger="click">
                        <el-form label-position="top" size="mini">
                          <el-form-item label="Number">
                            <el-input v-model="sms.number" />
                          </el-form-item>
                          <el-form-item label="Message">
                            <el-input v-model="sms.message" type="textarea" />
                          </el-form-item>
                          <el-form-item>
                            <el-button type="success" @click="sendSms">
                              Send SMS
                            </el-button>
                          </el-form-item>
                        </el-form>
                        <a slot="reference" @click.stop>Test SMS</a>
                      </el-popover>
                    </label>
                    <el-form-item label="Phone Number">
                      <el-input v-model="envs.twilio.phone_number" />
                    </el-form-item>
                    <el-form-item label="Account SID">
                      <el-input v-model="envs.twilio.account_sid" />
                    </el-form-item>
                    <el-form-item label="Auth Token">
                      <el-input v-model="envs.twilio.auth_token" />
                    </el-form-item>
                  </slot>
                  <el-divider />
                  <label class="title">
                    <div class="text-base">SMTP Settings</div>
                    <a @click="sendEmail">Test Email</a>
                  </label>
                  <el-form-item label="Address">
                    <el-input v-model="envs.smtp_settings.address" />
                  </el-form-item>
                  <el-form-item label="Port">
                    <el-input v-model="envs.smtp_settings.port" />
                  </el-form-item>
                  <el-form-item label="Username">
                    <el-input v-model="envs.smtp_settings.user_name" />
                  </el-form-item>
                  <el-form-item label="Password">
                    <el-input v-model="envs.smtp_settings.password" />
                  </el-form-item>
                  <el-form-item label="Authentication">
                    <el-tooltip
                      content="plain or login"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-input v-model="envs.smtp_settings.authentication" />
                    </el-tooltip>
                  </el-form-item>
                  <el-form-item>
                    <el-checkbox
                      v-model="envs.smtp_settings.enable_starttls_auto"
                      label="Enable SMTP/TLS"
                    />
                    <el-checkbox v-model="envs.smtp_settings.ssl" label="SSL" />
                    <el-checkbox
                      v-model="envs.smtp_settings.verify_none"
                      label="Verify None"
                    />
                  </el-form-item>
                  <el-divider />
                  <label class="title">
                    <div class="text-base">Traces</div>
                  </label>
                  <el-form-item label="Container">
                    <el-tooltip
                      content="Enable container ETA/LFD auto trace"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-checkbox v-model="envs.trace" label="Auto Trace" />
                    </el-tooltip>
                    <el-tooltip
                      content="Container API tracing should automatically update ETA"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-checkbox
                        v-model="envs.trace_auto_update_eta"
                        label="Auto Update ETA"
                      />
                    </el-tooltip>
                    <el-tooltip
                      content="Container API tracing should automatically update order appointment"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-checkbox
                        v-model="envs.trace_auto_update_appt"
                        label="Auto Update Appt"
                      />
                    </el-tooltip>
                    <el-tooltip
                      content="Container API tracing should automatically update chassis number"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-checkbox
                        v-model="envs.trace_auto_update_chassis"
                        label="Auto Update Chassis"
                      />
                    </el-tooltip>
                    <el-tooltip
                      content="Container API tracing should exclude updating BNSF chassis number"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-checkbox
                        v-model="envs.trace_auto_update_exclude_bnsf_chassis"
                        label="Exclude BNSF Chassis"
                      />
                    </el-tooltip>
                    <el-tooltip
                      content="Container API tracing should automatically update interchange data"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-checkbox
                        v-model="envs.trace_auto_update_intchg"
                        label="Auto Update Interchange"
                      />
                    </el-tooltip>
                  </el-form-item>
                  <el-form-item label="Traffic">
                    <el-radio
                      v-model="envs.autotrace.traffic"
                      label="light"
                      :disabled="!envs.trace"
                      >Light</el-radio
                    >
                    <el-radio
                      v-model="envs.autotrace.traffic"
                      label="heavy"
                      :disabled="!envs.trace"
                      >Heavy</el-radio
                    >
                  </el-form-item>
                  <el-form-item label="Auto Hours">
                    <el-input
                      v-model="envs.autotrace.hours"
                      :disabled="!envs.trace"
                      style="width: 400px"
                    >
                      <template slot="append"
                        >E.g. 6,17 (every 6am and 5pm)</template
                      >
                    </el-input>
                  </el-form-item>
                  <el-form-item label="Chassis Pools">
                    <el-tooltip
                      content="Trace chassis info (options: dcli,trac,cocp)"
                      placement="right"
                      :open-delay="1000"
                    >
                      <el-input
                        v-model="envs.chassis_pools"
                        :disabled="!envs.trace"
                      />
                    </el-tooltip>
                  </el-form-item>
                  <el-divider />
                  <label class="flex items-center justify-between">
                    <div class="text-base">StreeTurn.Net</div>
                  </label>
                  <el-form-item label="Username">
                    <el-input v-model="envs.streetturn.username" />
                  </el-form-item>
                  <el-form-item label="Password">
                    <el-input v-model="envs.streetturn.password" />
                  </el-form-item>
                  <el-divider />
                  <el-form-item>
                    <el-button type="primary" @click="saveEnvs"
                      >Save Settings</el-button
                    >
                  </el-form-item>
                </el-form>
              </el-col>
            </el-row>
          </el-tab-pane>
          <el-tab-pane label="Users" name="users">
            <slot v-if="tenant.users">
              <div class="grid grid-cols-4 gap-1">
                <el-card
                  shadow="hover"
                  class="dense"
                  v-for="(users, name) in tenant.users"
                  :key="name"
                >
                  <div slot="header" class="flex-space">
                    <div class="capitalize">{{ name }}</div>
                    <div>{{ users.length }}</div>
                  </div>
                  <ul class="circle h500">
                    <li v-for="(user, index) in users" :key="index">
                      <div class="flex items-center justify-between">
                        <a
                          v-if="user.id"
                          @click="loginByUser(user)"
                          class="normal"
                          >{{ user.name }}</a
                        >
                        <span v-else>{{ user.name }} (w/o user)</span>
                        <div v-if="user.description" class="tag">
                          {{ user.description }}
                        </div>
                      </div>
                    </li>
                  </ul>
                </el-card>
              </div>
            </slot>
          </el-tab-pane>
          <el-tab-pane label="Mobiles" name="mobiles">
            <slot v-if="tenant.mobiles">
              <v-table
                name="tenant-mobiles"
                :items="tenant.mobiles"
                :row-height="30"
                :coreColumnDefs="mobileColumnDefs"
                :defaultSort="{ prop: 'user', order: 'ascending' }"
                :getHeight="getMobileTableHeight"
              >
              </v-table>
            </slot>
          </el-tab-pane>
          <el-tab-pane label="Braintree" name="braintree">
            <el-row :gutter="20">
              <el-col :xs="24" :sm="20" :md="12" :lg="12" :xl="6">
                <el-form label-position="top" size="mini">
                  <el-form-item label="Customer ID">
                    <el-input v-model="braintree.customer_id" />
                  </el-form-item>
                  <el-form-item label="Subscription ID">
                    <el-input v-model="braintree.subscription_id" />
                  </el-form-item>
                  <el-form-item label="Tier Plan" required>
                    <el-select
                      v-model="braintree.addon_id"
                      filterable
                      style="width: 100%"
                    >
                      <el-option
                        v-for="tier in addOns"
                        :key="tier.id"
                        :label="`${tier.name} ($${tier.amount})`"
                        :value="tier.id"
                      >
                      </el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="Monthly Fee Function">
                    <el-input v-model="braintree.monthly_fee_function" />
                  </el-form-item>
                  <el-form-item>
                    <el-button type="primary" @click="saveBraintree">
                      Save Braintree Settings
                    </el-button>
                  </el-form-item>
                </el-form>
              </el-col>
            </el-row>
          </el-tab-pane>
          <el-tab-pane label="Agent" name="agent" lazy>
            <AgentWidget :tenant="{ subdomain: tenant.subdomain }" />
          </el-tab-pane>
        </el-tabs>
      </slot>
    </div>
  </div>
</template>
<script>
import AgentWidget from "@/system/pages/agent/widget.vue";
export default {
  name: "Tenants",
  components: {
    AgentWidget,
  },
  data() {
    return {
      activePlan: "paid",
      tenants: [],
      tenant: {},
      envs: {},
      sms: {},
      options: {
        tags: [
          "geotab_vehicle_stats_feed",
          "geotab_auto_vehicle_connection",
          "samsara_vehicle_stats_feed",
          "samsara_auto_vehicle_connection",
          "maintenances_limited",
        ],
      },
      states: [],
      braintree: {},
      addOns: [],
      mobileColumnDefs: [
        {
          name: "User",
          field: "user",
          width: "200",
          component: "v-table-column",
        },
        {
          name: "Role",
          field: "role",
          width: "100",
          component: "v-table-column",
        },
        {
          name: "Platform",
          field: "platform",
          width: "200",
          component: "v-table-column",
        },
        {
          name: "Version",
          field: "version",
          width: "100",
          component: "v-table-column",
        },
        {
          name: "UUID",
          field: "uuid",
          width: "80",
          component: "v-table-column",
        },
        {
          name: "Last Logged",
          field: "logged_at",
          width: "150",
          component: "v-table-column",
        },
        {
          name: "Last Located",
          field: "located_at",
          width: "150",
          component: "v-table-column",
        },
        {
          name: "Device ID",
          field: "device_id",
          width: "300",
          component: "v-table-column",
        },
      ],
    };
  },
  created() {
    this.loadData();
  },
  updated() {
    NApp.inputMask();
  },
  computed: {
    freeTenants() {
      return this.tenants.filter((tenant) => tenant.free);
    },
    paidTenants() {
      return this.tenants.filter((tenant) => !tenant.free);
    },
    planTabs() {
      return [
        { name: "paid", tenants: this.paidTenants },
        { name: "free", tenants: this.freeTenants },
      ];
    },
    maxTrackVersionCount() {
      return (
        this._.maxBy(this.tenants, "track_version.count")?.track_version
          ?.count || 100
      );
    },
    trackVersionProgress() {
      return function (tenant) {
        return (tenant.track_version.count / this.maxTrackVersionCount) * 100.0;
      };
    },
  },
  methods: {
    async destroy(item) {
      let response;
      try {
        const { id } = item;

        const {
          data: { message },
        } = await this.$http2.delete(`/system/tenants/${id}.json`);
        this.$message({ message, type: "success" });
        const index = this.tenants.findIndex((x) => x.id == item.id);
        this.tenants.splice(index, 1);
        this.tenant = {};
      } catch (ex) {
        console.error(ex);
        console.error(response);
      }
    },
    getMobileTableHeight() {
      return 600;
    },
    async loadData() {
      try {
        this.states = (
          await this.$http.get("/api/v1/options.json", {
            params: { target: "states" },
          })
        ).data;
        this.tenants = (await this.$http2.get("/system/tenants.json")).data;
        this.addOns = (
          await this.$http2.get("/api/v1/subscriptions/add_ons", {
            params: { include_grandfather: true },
          })
        ).data;
      } catch (ex) {
        console.error(ex);
      }
    },
    async add() {
      this.tenant = Object.assign({}, { id: 0 });
      if (this.options.timezones == undefined) {
        try {
          const { data } = await this.$http.get("/api/v1/options.json", {
            params: { target: "timezones" },
          });
          this.$set(this.options, "timezones", data);
        } catch (err) {
          // ignore
        }
      }
    },
    async create() {
      try {
        const { data } = await this.$http.post("/system/tenants.json", {
          tenant: this.tenant,
        });
        this.tenant = {};
        this.$message({ message: data.message, type: "success" });
      } catch (err) {
        this.$set(this.tenant, "errors", err.response?.data?.errors);
      }
    },
    async update(item) {
      const {
        id,
        active,
        directory,
        name,
        us_dot_number,
        legacy_us_dot_numbers,
        tags,
      } = item;
      try {
        const { data } = await this.$http.put(`/system/tenants/${id}.json`, {
          tenant: {
            active,
            directory,
            name,
            us_dot_number,
            legacy_us_dot_numbers,
            tags,
          },
        });
        const index = this.tenants.findIndex((o) => o.id == data.id);
        this.tenants.splice(index, 1, data);
        this.$message({ message: "Saved successfully", type: "success" });
      } catch (err) {
        // ignore
      }
    },
    summary() {
      this.getDownload("/system/tenants/summary.csv");
    },
    async load(item) {
      try {
        this.tenant = Object.assign(
          {
            activeTab: "settings",
            envs: {},
          },
          (
            await this.$http2.get(
              `/system/tenants/${item.subdomain}/details.json`,
            )
          ).data,
          item,
        );
        this.envs = Object.assign(
          {
            twilio: {
              phone_number: "",
              account_sid: "",
              auth_token: "",
            },
          },
          {
            smtp_settings: {
              ssl: false,
              port: "",
              address: "",
              password: "",
              user_name: "",
              verify_none: false,
              authentication: "",
              enable_starttls_auto: false,
            },
          },
          {
            streetturn: {
              username: "",
              password: "",
            },
          },
          {
            autotrace: {},
          },
          {
            document_ai_options: [],
          },
          this.tenant.envs,
        );
        this.braintree = Object.assign({}, this.tenant.braintree);
      } catch (ex) {
        console.error(ex);
      }
    },
    openSubdomain(item) {
      window.open(
        window.location.origin.replace(/(:\/\/[^.]*)/, `://${item.subdomain}`),
      );
    },
    async saveBraintree() {
      try {
        await this.$http2.patch(
          `/system/tenants/${this.tenant.subdomain}/braintree.json`,
          { braintree: this.braintree },
        );
        this.$message({ message: "Saved successfully", type: "success" });
      } catch (ex) {
        console.error(ex);
      }
    },
    async saveEnvs() {
      try {
        await this.$http2.post(
          `/system/tenants/${this.tenant.subdomain}/envs.json`,
          {
            envs: this.envs,
          },
        );
        this.$message({ message: "Saved successfully", type: "success" });
      } catch (ex) {
        console.error(ex);
      }
    },
    async sendSms() {
      try {
        await this.$http.post(
          `/system/tenants/${this.tenant.subdomain}/sms.json`,
          { sms: this.sms },
        );
        this.sms = {};
        this.$message({
          message: "Check your text message",
          type: "success",
        });
      } catch (err) {
        this.$message({
          message: err.response?.data?.errors ?? err.message,
          type: "error",
        });
      }
    },
    async sendEmail() {
      try {
        await this.$http.post(
          `/system/tenants/${this.tenant.subdomain}/email.json`,
        );
        this.$message({
          message: "Check your email",
          type: "success",
        });
      } catch (err) {
        this.$message({
          message: err.response?.data?.errors ?? err.message,
          type: "error",
        });
      }
    },
    async loginByUser(user) {
      try {
        const response = await this.$http.get(
          `/system/users/${user.id}.json?tenant=${this.tenant.subdomain}`,
        );
        const url = response.data.url;
        window.open(url, "_blank");
      } catch (ex) {
        console.error(ex);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.tenant::v-deep {
  @apply relative;
  & > .el-tabs > .el-tabs__content {
    @apply h-[calc(100vh_-_160px)] overflow-auto #{!important};
  }
  fieldset {
    @apply px-2 grid grid-cols-4;

    .el-checkbox__label {
      @apply text-xs;
    }
  }
  .title {
    @apply flex items-center justify-between bg-sky-50 p-1.5 mb-2;
  }
  .tenant-name {
    @apply absolute top-1.5 right-2 z-50 bg-slate-200 px-2 py-1 rounded font-bold;
  }
}
</style>
